import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@api/v1/client'
import DataGrid from '@components/common/DataGrid'
import LoadingSpinner from '@components/common/LoadingSpinner'
import DateRangeFilter from '@components/reports/DateRangeFilter'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const TABS = [
  { id: 'payments', label: 'Payments' },
  { id: 'payouts', label: 'Payouts' },
  { id: 'trust', label: 'Trust Accounting' },
  { id: 'settings', label: 'Settings' },
]

const STATUS_BADGE = {
  succeeded: 'text-emerald-700 bg-emerald-50',
  approved: 'text-emerald-700 bg-emerald-50',
  settled: 'text-emerald-700 bg-emerald-50',
  funded: 'text-emerald-700 bg-emerald-50',
  paid: 'text-emerald-700 bg-emerald-50',
  pending: 'text-amber-700 bg-amber-50',
  processing: 'text-blue-700 bg-blue-50',
  requires_action: 'text-amber-700 bg-amber-50',
  failed: 'text-red-700 bg-red-50',
  rejected: 'text-red-700 bg-red-50',
  reversed: 'text-red-700 bg-red-50',
  refunded: 'text-red-700 bg-red-50',
}

const fmtCurrency = (v) => {
  const n = Number(v)
  if (isNaN(n)) return '$0.00'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const StatusBadge = ({ status }) => {
  const key = (status || 'pending').toLowerCase().replace(/\s+/g, '_')
  const cls = STATUS_BADGE[key] || 'text-gray-600 bg-gray-100'
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>
      {(status || 'Unknown').replace(/_/g, ' ')}
    </span>
  )
}

/*
 * CC-53132: what a read that NEVER COMPLETED looks like, as distinct from a read
 * that came back empty.
 *
 * `blocked` is the state that lies. With react-query's default
 * `networkMode: 'online'` the query is PAUSED while `onlineManager` believes the
 * browser is offline: `queryFn` is never called, nothing throws, nothing is
 * logged, and `data` stays `undefined`. `isLoading` is `isPending && isFetching`,
 * so it is FALSE — a page keyed on `!isLoading` therefore skips its spinner and
 * goes straight to describing data it never received. On this page that read as
 * "0 payments totaling $0.00", four $0.00 stat cards and an empty trust ledger,
 * indefinitely. In a billing application that is the same defect as a wrong
 * number: the firm's money is not missing, we simply never asked.
 *
 * `role="alert"` because the user is looking at a screen that appeared to answer
 * their question and has to be told that it did not. The retry is the point, not
 * decoration: a paused query resumes on its own only when the browser fires an
 * `online` event, so without a button the only exit is a full reload.
 */
const UnresolvedRead = ({ state, subject, error, onRetry }) => {
  const blocked = state === 'blocked'
  return (
    <div
      role="alert"
      className={`rounded-xl border p-6 text-sm ${
        blocked
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-red-100 bg-red-50 text-red-700'
      }`}
    >
      <p className="font-medium">
        {blocked
          ? `Can’t reach the server, so we haven’t loaded your ${subject}.`
          : `We couldn’t load your ${subject}: ${error?.message}`}
      </p>
      <p className={`mt-1 ${blocked ? 'text-amber-800' : ''}`}>
        This is not an empty list, and the totals are not zero — nothing was read, so no figure
        on this tab is being shown at all.
        {blocked ? ' Your connection looks offline; check it and try again.' : ''}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${
          blocked
            ? 'border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'border-red-200 text-red-700 hover:bg-red-100'
        }`}
      >
        Try again
      </button>
    </div>
  )
}

const StatCard = ({ label, value, sublabel, accent }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
    <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
    <p className={`text-2xl font-semibold font-display ${accent ? 'text-cb-accent-text' : 'text-gray-900'}`}>
      {value}
    </p>
    {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
  </div>
)

const Payments = () => {
  useDocumentTitle('Payments')
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('payments')
  const [dateRange, setDateRange] = useState({
    from: dayjs().startOf('month').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  })
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all')

  // Payments via objects endpoint (type 1009)
  const paymentsQuery = useQuery({
    queryKey: ['payments-list'],
    queryFn: () => api.get('/objects/1009', { limit: 1000 }),
  })
  const paymentsResp = paymentsQuery.data
  /*
   * CC-53132. `isLoading` cannot tell "still loading" from "never asked", so the
   * five-way state is what drives every branch below. See `@api/v1/readState`;
   * the hook (rather than the bare function) is used because it carries the
   * permanent blocked-read diagnostic — a paused fetch leaves NO trace otherwise,
   * on the client or the server, which is exactly why this class of bug survives
   * clean server-side investigations.
   *
   * `idle` is UNREACHABLE for this query: it has no `enabled` option, so it is
   * always switched on. It is nevertheless folded into the loading branch rather
   * than left unhandled, because every branch here is gated on a named state and
   * an unhandled fifth one renders NOTHING — a blank page for whoever adds an
   * `enabled` gate later. Cheaper to cover than to leave as a trap.
   */
  const paymentsState = useReadState('payments-list', paymentsQuery)

  // Payment distributions (type 33) for payout reconciliation
  const distribQuery = useQuery({
    queryKey: ['payment-distributions'],
    queryFn: () => api.get('/objects/33', { limit: 500 }),
    enabled: activeTab === 'payouts' || activeTab === 'trust',
  })
  const distribResp = distribQuery.data
  /*
   * This query IS conditionally enabled, so `idle` is genuinely reachable — but
   * never at the place it is rendered. Its `enabled` condition and the Payouts
   * panel's render condition are the same expression on the same `activeTab`
   * state, so by the time anything below is on screen the query is switched on
   * and react-query has moved it to fetching (or, offline, to paused → blocked).
   * That is what makes folding `idle` into loading safe here: it can only ever be
   * the first frame, never a permanent spinner. If the enablement is ever widened
   * away from the tab, revisit this — `idle` would then mean "not asked yet",
   * which is an ordinary state and must NOT be dressed up as loading or as an
   * error.
   */
  const distribState = useReadState('payment-distributions', distribQuery)

  // Invoices for cross-referencing
  const { data: invoicesResp } = useQuery({
    queryKey: ['invoices-for-payments'],
    queryFn: () => api.get('/objects/30', { limit: 500 }),
    enabled: activeTab === 'payouts' || activeTab === 'trust',
  })

  /*
   * These deliberately do NOT default to `[]`. `undefined` means "we have no
   * answer"; `[]` means "the server said there are none". Collapsing the two with
   * `|| []` is the defect itself — it is what let a paused read render as a firm
   * with no payments. The `undefined` is propagated through every memo below so
   * that each render site is forced to decide, rather than silently inheriting a
   * zero.
   *
   * `paymentFields` and `invoices` keep their `|| []`: neither is ever described
   * to the user (fields only build a lookup map; `invoices` is not rendered at
   * all), so there is no claim for an empty array to falsify.
   */
  const rawPayments = paymentsResp?.data
  const paymentFields = paymentsResp?.meta?.fields || []
  const distributions = distribResp?.data
  const invoices = invoicesResp?.data || []

  // Build a field lookup map for dynamic fields
  const fieldMap = useMemo(() => {
    const map = {}
    for (const f of paymentFields) {
      map[f.id] = f
    }
    return map
  }, [paymentFields])

  // Normalize payment records with known field mappings.
  // `undefined` in, `undefined` out — see the note on `rawPayments`.
  const payments = useMemo(() => {
    return rawPayments?.map((p) => {
      // Try to find amount, date, type, reference fields by label
      let amount = 0
      let dateReceived = ''
      let paymentType = ''
      let refNum = ''
      let contact = ''
      let matter = ''
      let status = 'succeeded'
      let convFee = 0
      let isTrust = false

      for (const [fid, val] of Object.entries(p)) {
        if (fid === 'id' || fid === 'name') continue
        const field = fieldMap[fid]
        if (!field) continue
        const lbl = (field.label || '').toLowerCase()

        if (lbl.includes('amount') && !lbl.includes('fee') && !lbl.includes('unapplied')) {
          amount = Number(val) || 0
        }
        if (lbl.includes('date') && lbl.includes('received')) {
          dateReceived = val
        }
        if (lbl === 'payment type' || lbl === 'type') {
          paymentType = String(val || '')
        }
        if (lbl.includes('reference')) {
          refNum = String(val || '')
        }
        if (lbl.includes('contact') || lbl.includes('received from')) {
          contact = String(val || '')
        }
        if (lbl.includes('matter') || lbl.includes('case')) {
          matter = String(val || '')
        }
        if (lbl.includes('convenience') && lbl.includes('fee')) {
          convFee = Number(val) || 0
        }
        if (lbl.includes('trust')) {
          isTrust = Boolean(val)
        }
        if (lbl.includes('reversal') && val) {
          status = 'reversed'
        }
      }

      return {
        ...p,
        _amount: amount,
        _dateReceived: dateReceived,
        _paymentType: paymentType,
        _refNum: refNum,
        _contact: contact,
        _matter: matter,
        _status: status,
        _convFee: convFee,
        _isTrust: isTrust,
      }
    })
  }, [rawPayments, fieldMap])

  // Filter payments by date and type. Still `undefined` if nothing was read —
  // filtering nothing must not manufacture an empty result set.
  const filtered = useMemo(() => {
    return payments?.filter((p) => {
      if (dateRange.from && p._dateReceived) {
        const d = dayjs(p._dateReceived)
        if (d.isBefore(dateRange.from)) return false
        if (dateRange.to && d.isAfter(dateRange.to)) return false
      }
      if (paymentTypeFilter !== 'all') {
        const t = p._paymentType.toLowerCase()
        if (paymentTypeFilter === 'card' && !t.includes('card') && !t.includes('credit')) {
          return false
        }
        if (paymentTypeFilter === 'ach' && !t.includes('ach') && !t.includes('bank')) {
          return false
        }
        if (paymentTypeFilter === 'check' && !t.includes('check')) return false
        if (paymentTypeFilter === 'trust' && !p._isTrust) return false
      }
      return true
    })
  }, [payments, dateRange, paymentTypeFilter])

  // Summary stats — `null`, not a row of zeroes, when there is nothing to
  // summarise. Every consumer below is gated on `stats` rather than on the read
  // state, because that is the value-level fact: a read can be `ready` and still
  // carry a payload with no `data` array, and describing THAT as "$0.00
  // received" is the same lie by a different route.
  const stats = useMemo(() => {
    if (!filtered) return null
    const total = filtered.reduce((s, p) => s + p._amount, 0)
    const fees = filtered.reduce((s, p) => s + p._convFee, 0)
    const trustTotal = filtered.filter((p) => p._isTrust).reduce((s, p) => s + p._amount, 0)
    const cardCount = filtered.filter((p) => {
      const t = p._paymentType.toLowerCase()
      return t.includes('card') || t.includes('credit')
    }).length
    const achCount = filtered.filter((p) => {
      const t = p._paymentType.toLowerCase()
      return t.includes('ach') || t.includes('bank')
    }).length
    return { total, fees, trustTotal, cardCount, achCount, count: filtered.length }
  }, [filtered])

  // Payout data — group payments by settled date (approximate from distributions)
  const payouts = useMemo(() => {
    // Unread distributions are not "no settlements" — keep them undefined so the
    // settlement count and the settled/fee totals below have nothing to render.
    if (!distributions) return undefined
    if (distributions.length === 0) return []
    const byDate = {}
    for (const d of distributions) {
      const date = d.distributionDate || d.date || d.name
      const key = date ? dayjs(date).format('YYYY-MM-DD') : 'unknown'
      if (!byDate[key]) {
        byDate[key] = { date: key, payments: [], totalAmount: 0, feeTotal: 0, expenseTotal: 0 }
      }
      const amt = Number(d.amount || 0)
      byDate[key].payments.push(d)
      byDate[key].totalAmount += amt
      byDate[key].feeTotal += Number(d.feeTotal || 0)
      byDate[key].expenseTotal += Number(d.expenseTotal || 0)
    }
    return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date))
  }, [distributions])

  // Trust transactions — payments marked as trust. Undefined-preserving: an
  // unread payments list must never render as a trust ledger with no entries and
  // a $0.00 balance, which is a client-funds statement.
  const trustPayments = useMemo(() => {
    return payments?.filter((p) => p._isTrust)
  }, [payments])

  // Payment columns
  const paymentColumns = useMemo(() => [
    {
      id: 'date',
      header: 'Date',
      accessorKey: '_dateReceived',
      size: 100,
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('M/D/YY') : ''
      },
    },
    { id: 'name', header: 'Payment', accessorKey: 'name', size: 200 },
    { id: 'contact', header: 'From', accessorKey: '_contact', size: 160 },
    { id: 'matter', header: 'Matter', accessorKey: '_matter', size: 160 },
    {
      id: 'type',
      header: 'Type',
      accessorKey: '_paymentType',
      size: 110,
      cell: ({ getValue }) => {
        const t = (getValue() || '').toLowerCase()
        let icon = '💳'
        if (t.includes('ach') || t.includes('bank')) icon = '🏦'
        else if (t.includes('check')) icon = '📝'
        else if (t.includes('cash')) icon = '💵'
        return <span className="text-sm">{icon} {getValue() || '--'}</span>
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: '_amount',
      size: 120,
      cell: ({ getValue }) => (
        <span className="font-medium text-gray-900">{fmtCurrency(getValue())}</span>
      ),
    },
    {
      id: 'fee',
      header: 'Conv. Fee',
      accessorKey: '_convFee',
      size: 100,
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? fmtCurrency(v) : '--'
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: '_status',
      size: 110,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    { id: 'ref', header: 'Reference', accessorKey: '_refNum', size: 130 },
  ], [])

  // Payout columns
  const payoutColumns = useMemo(() => [
    {
      id: 'date',
      header: 'Settlement Date',
      accessorKey: 'date',
      size: 130,
      cell: ({ getValue }) => {
        const v = getValue()
        return v && v !== 'unknown' ? dayjs(v).format('MMM D, YYYY') : '--'
      },
    },
    {
      id: 'count',
      header: 'Transactions',
      size: 110,
      cell: ({ row }) => row.original.payments.length,
    },
    {
      id: 'fees',
      header: 'Fees',
      accessorKey: 'feeTotal',
      size: 120,
      cell: ({ getValue }) => fmtCurrency(getValue()),
    },
    {
      id: 'expenses',
      header: 'Expenses',
      accessorKey: 'expenseTotal',
      size: 120,
      cell: ({ getValue }) => fmtCurrency(getValue()),
    },
    {
      id: 'total',
      header: 'Net Amount',
      accessorKey: 'totalAmount',
      size: 130,
      cell: ({ getValue }) => (
        <span className="font-medium text-gray-900">{fmtCurrency(getValue())}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      size: 100,
      cell: () => <StatusBadge status="paid" />,
    },
  ], [])

  // Trust columns
  const trustColumns = useMemo(() => [
    {
      id: 'date',
      header: 'Date',
      accessorKey: '_dateReceived',
      size: 100,
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('M/D/YY') : ''
      },
    },
    { id: 'name', header: 'Transaction', accessorKey: 'name', size: 200 },
    { id: 'contact', header: 'Client', accessorKey: '_contact', size: 160 },
    { id: 'matter', header: 'Matter', accessorKey: '_matter', size: 160 },
    {
      id: 'type',
      header: 'Type',
      accessorKey: '_paymentType',
      size: 120,
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: '_amount',
      size: 130,
      cell: ({ getValue }) => (
        <span className="font-medium text-gray-900">{fmtCurrency(getValue())}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: '_status',
      size: 100,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
  ], [])

  const handleRowClick = useCallback((row) => {
    navigate(`/payments/${row.id}`)
  }, [navigate])

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Payments</h1>
          {/* CC-53132: the subtitle is a count AND a sum of money. Both are
              claims about what the firm received, so neither may be rendered
              off a read that has not finished — "0 payments totaling $0.00" is
              indistinguishable from a quiet month, and a paused read produced
              exactly that string with no spinner and no error. Omitted rather
              than zeroed; the tab below says what actually happened. */}
          {stats && (
            <p className="text-sm text-gray-500 mt-1">
              {stats.count} payment{stats.count !== 1 ? 's' : ''} totaling {fmtCurrency(stats.total)}
            </p>
          )}
        </div>
      </div>

      {/* Summary Cards — same rule as the subtitle, and the reason the whole row
          goes rather than each card individually: four cards reading $0.00 / 0
          are one composite claim ("nothing came in this month"), and there is no
          honest way to show three quarters of it. */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Received" value={fmtCurrency(stats.total)} />
          <StatCard
            label="Convenience Fees"
            value={fmtCurrency(stats.fees)}
            sublabel={stats.fees > 0 ? `on ${stats.count} payments` : undefined}
          />
          <StatCard
            label="Trust Deposits"
            value={fmtCurrency(stats.trustTotal)}
            sublabel={`${filtered.filter((p) => p._isTrust).length} transactions`}
          />
          <StatCard
            label="Payment Methods"
            value={`${stats.cardCount + stats.achCount}`}
            sublabel={`${stats.cardCount} card, ${stats.achCount} ACH`}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DateRangeFilter value={dateRange} onChange={setDateRange} />
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Payment Type
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'card', label: 'Card' },
                    { id: 'ach', label: 'ACH' },
                    { id: 'check', label: 'Check' },
                    { id: 'trust', label: 'Trust' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setPaymentTypeFilter(opt.id)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        paymentTypeFilter === opt.id
                          ? 'bg-cb-accent text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* All five read states land somewhere: ready → the grid; loading and
              the unreachable idle → the grid's spinner; blocked and failed → the
              panel, IN PLACE OF the grid, so there is exactly one thing on screen
              telling the user what happened. `data` still passes `undefined`
              straight through rather than `|| []` — DataGrid distinguishes the
              two itself, which keeps the honest message the default if a state
              ever escapes the branch below. */}
          {paymentsState === 'blocked' || paymentsState === 'failed' ? (
            <UnresolvedRead
              state={paymentsState}
              subject="payments"
              error={paymentsQuery.error}
              onRetry={() => paymentsQuery.refetch()}
            />
          ) : (
            <DataGrid
              columns={paymentColumns}
              data={filtered}
              isLoading={paymentsState === 'loading' || paymentsState === 'idle'}
              onRowClick={handleRowClick}
              emptyMessage="No payments match your filters"
              loadingMessage="Loading payments..."
              unresolvedMessage="These payments could not be loaded, so this is not known to be empty."
              exportFilename="payments"
              pageSize={50}
            />
          )}
        </div>
      )}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <div className="space-y-4">
          {/* Payout Summary — a settlement count and two money totals, so it is
              gated exactly like the payments cards. "0 settlements, $0.00
              settled" is what a firm sees on a quiet week; it must not also be
              what they see when the distributions were never fetched. */}
          {payouts && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Settlements</p>
                <p className="text-2xl font-semibold text-gray-900 font-display">{payouts.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Settled</p>
                <p className="text-2xl font-semibold text-gray-900 font-display">
                  {fmtCurrency(payouts.reduce((s, p) => s + p.totalAmount, 0))}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Fees Collected</p>
                <p className="text-2xl font-semibold text-gray-900 font-display">
                  {fmtCurrency(payouts.reduce((s, p) => s + p.feeTotal, 0))}
                </p>
              </div>
            </div>
          )}

          {distribState === 'blocked' || distribState === 'failed' ? (
            <UnresolvedRead
              state={distribState}
              subject="payout settlements"
              error={distribQuery.error}
              onRetry={() => distribQuery.refetch()}
            />
          ) : (
            <DataGrid
              columns={payoutColumns}
              data={payouts}
              isLoading={distribState === 'loading' || distribState === 'idle'}
              emptyMessage="No payout settlements found"
              loadingMessage="Loading payouts..."
              unresolvedMessage="These settlements could not be loaded, so this is not known to be empty."
              exportFilename="payouts"
              pageSize={25}
            />
          )}
        </div>
      )}

      {/* Trust Accounting Tab */}
      {activeTab === 'trust' && (
        <div className="space-y-4">
          {/* Trust Summary — the highest-stakes count on the page. A trust
              balance is client money a firm is legally accountable for, so
              "0 transactions, $0.00" for a read that never happened is the worst
              version of this bug, not a cosmetic one. The two derived cards are
              gated; the third states a configuration fact, not a figure, and is
              left alone. */}
          {trustPayments && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-sm font-medium text-gray-500 mb-1">Trust Transactions</p>
                <p className="text-2xl font-semibold text-gray-900 font-display">
                  {trustPayments.length}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-sm font-medium text-gray-500 mb-1">Trust Balance</p>
                <p className="text-2xl font-semibold text-gray-900 font-display">
                  {fmtCurrency(trustPayments.reduce((s, p) => s + p._amount, 0))}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-cb-teal" />
                  <p className="text-sm font-medium text-gray-500">Trust Account</p>
                </div>
                <p className="text-2xl font-semibold text-gray-900 font-display">Active</p>
              </div>
            </div>
          )}

          {/* Same read as the Payments tab — this ledger is a filtered view of
              it — so it reports the same state, with trust-specific wording. */}
          {paymentsState === 'blocked' || paymentsState === 'failed' ? (
            <UnresolvedRead
              state={paymentsState}
              subject="trust transactions"
              error={paymentsQuery.error}
              onRetry={() => paymentsQuery.refetch()}
            />
          ) : (
            <DataGrid
              columns={trustColumns}
              data={trustPayments}
              isLoading={paymentsState === 'loading' || paymentsState === 'idle'}
              onRowClick={handleRowClick}
              emptyMessage="No trust transactions found"
              loadingMessage="Loading trust transactions..."
              unresolvedMessage="These trust transactions could not be loaded, so this is not known to be empty."
              exportFilename="trust-transactions"
              pageSize={50}
            />
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <h2 className="text-base font-semibold text-gray-900">Stripe Connect</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Operating Account */}
              <div className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">Operating Account</h3>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    Connected
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Account Type</span>
                    <span className="text-gray-900 font-medium">Operating</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Accepts</span>
                    <span className="text-gray-900 font-medium">Card, ACH</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Processing Fee GL</span>
                    <span className="text-gray-900 font-medium">Configured</span>
                  </div>
                </div>
              </div>

              {/* Trust Account */}
              <div className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">Trust Account</h3>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    Connected
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Account Type</span>
                    <span className="text-gray-900 font-medium">Trust / IOLTA</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Accepts</span>
                    <span className="text-gray-900 font-medium">Card, ACH</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Trust GL Account</span>
                    <span className="text-gray-900 font-medium">Configured</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <h2 className="text-base font-semibold text-gray-900">Payment Configuration</h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="border border-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Accepted Methods</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-5 bg-gradient-to-r from-blue-600 to-blue-400 rounded text-[8px] text-white flex items-center justify-center font-bold">
                      VISA
                    </div>
                    <span className="text-sm text-gray-700">Visa</span>
                    <span className="ml-auto text-xs text-emerald-600">Enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-5 bg-gradient-to-r from-red-600 to-orange-500 rounded text-[8px] text-white flex items-center justify-center font-bold">
                      MC
                    </div>
                    <span className="text-sm text-gray-700">Mastercard</span>
                    <span className="ml-auto text-xs text-emerald-600">Enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-5 bg-gradient-to-r from-blue-700 to-blue-500 rounded text-[8px] text-white flex items-center justify-center font-bold">
                      AMEX
                    </div>
                    <span className="text-sm text-gray-700">American Express</span>
                    <span className="ml-auto text-xs text-emerald-600">Enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-5 bg-gray-200 rounded text-[8px] text-gray-600 flex items-center justify-center font-bold">
                      ACH
                    </div>
                    <span className="text-sm text-gray-700">Bank Transfer (ACH)</span>
                    <span className="ml-auto text-xs text-emerald-600">Enabled</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Fee Structure</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Card Processing</span>
                    <span className="text-gray-900 font-medium">2.9% + $0.30</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">ACH Processing</span>
                    <span className="text-gray-900 font-medium">0.8%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Convenience Fee</span>
                    <span className="text-gray-900 font-medium">3.0%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Fee Passed to Client</span>
                    <span className="text-emerald-600 font-medium">Yes</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Payment Rules</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Partial Payments</span>
                    <span className="text-gray-900 font-medium">Allowed</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Multi-Invoice</span>
                    <span className="text-gray-900 font-medium">Enabled</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Auto-Apply</span>
                    <span className="text-gray-900 font-medium">Oldest First</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Environment</span>
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                      Test Mode
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Webhook Events */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Webhook Events</h2>
            <p className="text-sm text-gray-500">
              Stripe webhook events are processed automatically for payment status updates,
              payout settlements, and account changes.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { event: 'payment.approved', desc: 'Payment succeeded' },
                { event: 'payment.rejected', desc: 'Payment failed' },
                { event: 'payment.requires_action', desc: '3D Secure / verification' },
                { event: 'payout.paid', desc: 'Settlement complete' },
                { event: 'account.updated', desc: 'Account info changed' },
                { event: 'charge.captured', desc: 'Charge captured' },
                { event: 'payment.refunded', desc: 'Refund processed' },
                { event: 'account.requirements', desc: 'Verification needed' },
              ].map((wh) => (
                <div key={wh.event} className="border border-gray-100 rounded-lg p-3">
                  <p className="text-xs font-mono text-cb-accent-text">{wh.event}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{wh.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payments
